/**
 * 页面弹窗展示管理，主要功能：弹窗队列
 * 目前使用场景：臻有钱首页(临额下发弹窗、身份证清晰分不足confirm、身份证不一致)
 * "弹窗队列"说明：当页面已有弹窗展示时，此时调用 applyOpenDialog 添加至队列，待上一弹窗释放后，取队列首位展示
 */
import { ubtSendError } from '../ubt';

/**
 * 弹窗任务接口定义
 * @interface IOpenTask
 */
interface IOpenTask {
  /** 弹窗所属页面标识，通常为 location.hash */
  page: string;
  /** 执行弹窗展示的回调函数 */
  openTask: () => void;
}

/**
 * 弹窗任务队列
 * 存储等待展示的弹窗任务，采用先进先出(FIFO)原则
 * 当页面已有弹窗展示时，新的弹窗任务会被添加到此队列中等待
 */
let openTasksQueue: IOpenTask[] = [];

/**
 * 各页面弹窗状态记录表
 * key: 页面标识(通常为 location.hash)
 * value: true表示该页面当前有弹窗展示中，false表示无弹窗
 * 用于判断是否可以立即展示新弹窗，或需要加入队列等待
 */
const pagesHasPopupStatus: Record<string, boolean> = {};

/**
 * 请求展示弹窗(规避多弹窗的冲突)
 * @param forceOpen 是否强制展示，若forceOpen为true时，不必传task方法，可自行处理打开逻辑
 * @param openTask 调用处展示弹窗的函数实现
 * @param pageName 弹窗展示的页面标识(可选参数)
 */
function applyOpenDialog(forceOpen = false, openTask?: () => void, pageName = ''): void {
  // 获取页面标识，优先使用传入的pageName，否则使用当前页面hash
  const page = pageName || window.location.hash;

  // 情况1: 强制展示 或 页面当前无弹窗 → 立即执行弹窗展示逻辑
  if (forceOpen || !pagesHasPopupStatus[page]) {
    try {
      // 执行弹窗展示回调函数
      if (typeof openTask === 'function') {
        openTask();
      }
      // 更新页面弹窗状态为"展示中"
      pagesHasPopupStatus[page] = true;
    } catch (err) {
      // 捕获弹窗展示过程中的异常，并上报错误
      ubtSendError(err as Error);
    }
  } else if (openTask) {
    openTasksQueue.push({ page, openTask });
  } else {
    ubtSendError(new Error('the params of "openTask" should be a function.'));
  }
}

/**
 * 可选展示弹窗(若页面已有弹窗，则不展示)
 * @param openTask 调用处展示弹窗的函数实现
 * @param pageName 弹窗展示的页面location.hash(可选参数)
 */
function optionalOpenDialog(openTask?: () => void, pageName = ''): boolean {
  const page = pageName || window.location.hash;

  // 仅在页面无弹窗且提供了有效展示函数时才执行
  if (!pagesHasPopupStatus[page] && typeof openTask === 'function') {
    try {
      // 执行弹窗展示逻辑
      openTask();
      // 更新页面弹窗状态
      pagesHasPopupStatus[page] = true;
      return true;
    } catch (err) {
      ubtSendError(err as Error);
    }
  }

  return false;
}

/**
 * 弹窗关闭通知 - 更新弹窗状态并处理队列中的下一个弹窗
 * @param timeout 延迟处理队列的时间(毫秒)
 *                - 默认500ms，给关闭动画和页面切换留出时间
 *                - 可根据实际动画时长调整
 * @param pageName 页面标识，默认使用 window.location.hash
 */
function closeDialogNotify(timeout = 500, pageName = ''): void {
  // 获取目标页面标识
  const targetPage = pageName || window.location.hash;

  // 立即清除页面弹窗状态
  pagesHasPopupStatus[targetPage] = false;

  setTimeout(() => {
    /**
     * 延迟500ms，再取队列的弹窗进行展示
     * 500ms，关闭弹窗动画、页面可能有切换
     * 若页面已跳转切换，则取消展示，并清空其他页面的弹窗展示任务
     */
    const currentPageHash = window.location.hash;
    if (currentPageHash !== targetPage) {
      /**
       * 页面切换后，清除非当前页面的弹窗任务
       * 这样做是为了避免在错误的页面展示弹窗
       */
      openTasksQueue = openTasksQueue.filter(({ page }) => page === currentPageHash);
    } else {
      /** 取该页面的弹窗队列首位，打开展示 */
      openTasksQueue.some(({ openTask, page }, idx) => {
        if (page === targetPage) {
          applyOpenDialog(false, openTask, page);
          openTasksQueue.splice(idx, 1);

          return true;
        }

        return false;
      });
    }
  }, timeout);
}

/**
 * 清除当前的页面弹窗状态
 */
function resetPageDialogStatus(pageName = ''): void {
  const currentPage = pageName || window.location.hash;

  // 检查该页面是否有弹窗状态需要清理
  if (pagesHasPopupStatus[currentPage]) {
    // 清除页面弹窗状态
    pagesHasPopupStatus[currentPage] = false;
    /**
     * 删除队列中该页面的所有弹窗任务
     * 过滤掉属于当前页面的任务，保留其他页面的任务
     */
    openTasksQueue = openTasksQueue.filter(({ page }) => page !== currentPage);
  }
}

export const dialogHelper: {
  /** 请求展示弹窗(支持队列管理和强制展示) */
  applyOpenDialog: typeof applyOpenDialog,
  /** 弹窗关闭通知(处理队列中的下一个弹窗) */
  closeDialogNotify: typeof closeDialogNotify,
  /** 可选展示弹窗(仅在无弹窗时展示，不支持队列) */
  optionalOpenDialog: typeof optionalOpenDialog,
  /** 重置页面弹窗状态(清理状态和队列任务) */
  resetPageDialogStatus: typeof resetPageDialogStatus,
} = {
  applyOpenDialog,
  closeDialogNotify,
  optionalOpenDialog,
  resetPageDialogStatus,
};
